// Copyright (c) Docugami, Inc. All rights reserved.

import { Dropdown, IDropdownOption, IDropdownStyles, IStyle } from "@fluentui/react";
import { userFontFamily } from "components/common/Fonts";
import { appBackgroundGrey } from "components/shell/presentation/AppStyles";
import { useAppSelector } from "hooks/redux";
import { ClusterDocument } from "networking/api";
import { useMemo } from "react";
import { ReportDocument } from "store/reports/selectors";
import { createGetDocSetDocuments } from "store/state_helpers";
import styled from "styled-components";

const Container = styled.div`
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
`

const commonFontStyling: IStyle = {
    fontFamily: userFontFamily,
    fontSize: '18px',
    fontWeight: 500,
};

const dropdownStyles: Partial<IDropdownStyles> = {
    dropdown: {
        width: '100%',
    },
    root: {
        width: '100%'
    },
    title: {
        background: appBackgroundGrey,
        border: 'none',
        ...commonFontStyling
    },
    dropdownItem: {
        ...commonFontStyling,
    },
    dropdownItemSelected: {
        ...commonFontStyling,
    }
}

interface DocsetDocumentPickerProps {
    docSetId: string;
    onDocumentSelected: (documentId: string) => void;
    documentsFilter?: (document: ClusterDocument) => boolean;
    dropdownLabel?: string;
}

const docToOption = (document: ClusterDocument): IDropdownOption<ClusterDocument> => ({
    key: document.folderId,
    text: document.filename,
    data: document,
    title: document.filename,
    disabled: !document.hasCpml
});

const getDocSetDocuments = createGetDocSetDocuments();
export const DocsetDocumentPicker = (props: DocsetDocumentPickerProps) => {
    const documents = useAppSelector(state => getDocSetDocuments(state, props.docSetId));
    const filteredDocuments = useMemo(() => !!props.documentsFilter ? documents.filter(props.documentsFilter) : documents, [documents, props.documentsFilter]);
    const documentOptions: IDropdownOption<ClusterDocument>[] = useMemo(() => filteredDocuments.map(docToOption), [filteredDocuments]);

    const onDocumentChange = (event: React.FormEvent<HTMLDivElement>, option: IDropdownOption<ReportDocument> | undefined) => {
        if (!!option && !!option.key) {
            props.onDocumentSelected(option.key as string);
        }
    };

    return (
        <Container>
            <Dropdown
                label={props.dropdownLabel}
                disabled={!props.docSetId || !filteredDocuments.length}
                dropdownWidth={'auto'}
                placeholder="Select a document"
                ariaLabel="document picker"
                styles={dropdownStyles}
                options={documentOptions}
                onChange={onDocumentChange}
            />
        </Container>
    )
}
